import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  buildChecklistPlainText,
  checklistMeta,
  flatChecklistItems,
  sectionsForMode,
} from '../data/yokoanChecklist';
import { AnalyticsEvents, trackEvent } from '../utils/analytics';
import { usePageMeta } from '../hooks/usePageMeta';
import '../styles/Checklist.css';

const STORAGE_KEY = 'miraijuku.yokoanChecklist.v1';
const MODE_KEY = 'miraijuku.yokoanChecklist.overseas';
const NOTE_KEY = 'miraijuku.yokoanChecklist.note';

const loadChecked = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
};

const Checklist = () => {
  const [checked, setChecked] = useState(() => loadChecked());
  const [overseas, setOverseas] = useState(() => {
    try {
      return localStorage.getItem(MODE_KEY) !== '0';
    } catch {
      return true;
    }
  });
  const [copied, setCopied] = useState(false);
  const [univNote, setUnivNote] = useState(() => {
    try {
      return localStorage.getItem(NOTE_KEY) || '';
    } catch {
      return '';
    }
  });

  usePageMeta({
    title: checklistMeta.title,
    description: checklistMeta.lede,
    path: '/checklist',
  });

  const sections = useMemo(() => sectionsForMode(overseas), [overseas]);
  const allItems = useMemo(() => flatChecklistItems(overseas), [overseas]);
  const done = allItems.filter((item) => checked.has(item.id)).length;
  const total = allItems.length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...checked]));
  }, [checked]);

  useEffect(() => {
    localStorage.setItem(MODE_KEY, overseas ? '1' : '0');
  }, [overseas]);

  useEffect(() => {
    localStorage.setItem(NOTE_KEY, univNote);
  }, [univNote]);

  const toggle = (id) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const reset = () => {
    if (window.confirm('체크를 모두 지울까요? (이 기기 저장만 초기화됩니다)')) {
      setChecked(new Set());
    }
  };

  const handleCopy = async () => {
    const text = [
      univNote.trim() ? `대학·학부 메모: ${univNote.trim()}` : '',
      buildChecklistPlainText(checked, overseas),
    ]
      .filter(Boolean)
      .join('\n\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      trackEvent(AnalyticsEvents.CHECKLIST_COPY, {
        overseas: overseas ? '1' : '0',
        done,
        total,
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="apple-page apple-checklist">
      <header className="apple-page-hero">
        <p className="apple-eyebrow">Checklist</p>
        <h1 className="apple-display">{checklistMeta.title}</h1>
        <p className="apple-lede">{checklistMeta.lede}</p>
        <p className="apple-checklist-meta">업데이트 {checklistMeta.updatedAt}</p>
      </header>

      <label className="apple-checklist-mode no-print">
        <input
          type="checkbox"
          checked={overseas}
          onChange={(e) => setOverseas(e.target.checked)}
        />
        해외출원(한국에서 지원) 항목 포함
      </label>

      <div className="apple-checklist-toolbar no-print">
        <div className="apple-checklist-progress" aria-live="polite">
          <div className="apple-checklist-progress-bar" style={{ width: `${pct}%` }} />
          <span>
            {done}/{total} · {pct}%
          </span>
        </div>
        <div className="apple-checklist-actions">
          <button type="button" className="apple-btn apple-btn-secondary" onClick={handleCopy}>
            {copied ? '복사됨' : '텍스트 복사'}
          </button>
          <button type="button" className="apple-btn apple-btn-secondary" onClick={() => window.print()}>
            인쇄
          </button>
          <button type="button" className="apple-btn apple-btn-secondary" onClick={reset}>
            초기화
          </button>
        </div>
      </div>

      <label className="apple-checklist-note">
        대학·학부 메모 (선택)
        <input
          type="text"
          value={univNote}
          onChange={(e) => setUnivNote(e.target.value)}
          placeholder="예: ○○대 공학부 · 2026 유학생 전형 PDF"
        />
      </label>

      <div className="apple-checklist-sections">
        {sections.map((section) => (
          <section key={section.id} className="apple-checklist-section">
            <h2>{section.title}</h2>
            <ul>
              {section.items.map((item) => {
                const isOn = checked.has(item.id);
                return (
                  <li key={item.id}>
                    <label className={isOn ? 'is-checked' : ''}>
                      <input
                        type="checkbox"
                        checked={isOn}
                        onChange={() => toggle(item.id)}
                      />
                      <span>{item.label}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>

      <aside className="apple-article-note apple-checklist-disclaimer">
        이 목록은 참고용입니다. 항목이 비어 있어도 요강에 다른 필수 조건이 있을 수 있습니다.
        지원 직전은 해당 연도 모집요항 원문으로 다시 대조하세요.
      </aside>

      <div className="apple-page-foot no-print">
        <Link to="/guides/yokoan-howto" className="apple-link">
          요강 읽는 법
        </Link>
        <Link to="/glossary" className="apple-link">
          용어집
        </Link>
        <Link to="/pass" className="apple-link">
          시즌패스
        </Link>
        <Link to="/finder" className="apple-btn apple-btn-primary">
          루트 파인더
        </Link>
      </div>
    </div>
  );
};

export default Checklist;
